import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import V4Tabs from './V4Tabs';

const DocumentsListV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [docs, setDocs] = useState({ items: [], total: 0 });
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerMarkdown, setViewerMarkdown] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [editorMode, setEditorMode] = useState(false);
  const [editorText, setEditorText] = useState('');
  const [editingDocId, setEditingDocId] = useState(null);
  const [viewerBaseDir, setViewerBaseDir] = useState('');
  const [widthPct, setWidthPct] = useState(50); // default 50%
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const [jobViewerOpen, setJobViewerOpen] = useState(false);
  const [jobDoc, setJobDoc] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const jobPollRef = useRef(null);
  const jobDragRef = useRef(null);
  const [jobWidthPct, setJobWidthPct] = useState(50);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset), q });
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/list?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      setDocs({ items: Array.isArray(data.items) ? data.items : [], total: data.total || 0 });
    } catch (_) {
      setDocs({ items: [], total: 0 });
    }
    setLoading(false);
  }, [getAuthHeaders, limit, offset, q]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Poll only rows that are in-progress to refresh status/progress
  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const items = Array.isArray(docs.items) ? docs.items : [];
        const active = items.filter((d) => ['queued','processing'].includes(String(d.status||'').toLowerCase()) && d.job_id);
        if (active.length === 0) return;
        const headers = { ...getAuthHeaders() };
        const updates = await Promise.all(active.map(async (d) => {
          try {
            const r = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${d.job_id}`, { headers });
            if (!r.ok) return null;
            const j = await r.json();
            return { id: d.id, job_id: d.job_id, status: j.status, progress: j.progress, metadata: j.metadata };
          } catch (_) { return null; }
        }));
        const map = new Map();
        for (const u of updates) if (u && u.id) map.set(u.id, u);
        if (map.size > 0) {
          setDocs((prev) => ({
            ...prev,
            items: (prev.items || []).map((d) => {
              const u = map.get(d.id);
              return u ? { ...d, status: u.status || d.status, progress: u.progress ?? d.progress } : d;
            })
          }));
        }
      } catch (_) { /* ignore */ }
    }, 5000);
    return () => clearInterval(timer);
  }, [docs.items, getAuthHeaders]);

  const viewMarkdown = async (doc) => {
    const url = doc.markdown_url ? `${API_BASE_URL}${doc.markdown_url}` : null;
    if (!url) return;
    try {
      setViewerTitle(doc.original_filename || 'Document');
      setViewerMarkdown('');
      setViewerLoading(true);
      setViewerOpen(true);
      setEditorMode(false);
      setEditingDocId(doc.id);
      const abs = new URL(url, window.location.origin);
      const baseDir = abs.pathname.substring(0, abs.pathname.lastIndexOf('/'));
      setViewerBaseDir(`${API_BASE_URL}${baseDir}`);
      const res = await fetch(url, { headers: { ...getAuthHeaders() } });
      const text = await res.text();
      setViewerMarkdown(text);
      setEditorText(text);
    } catch (e) {
      setViewerMarkdown(`Failed to load markdown. ${String(e)}`);
    }
    setViewerLoading(false);
  };

  const toggleEditor = () => {
    setEditorMode((v) => !v);
  };

  const saveMarkdown = async () => {
    if (!editingDocId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/${editingDocId}/markdown`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ markdown: editorText })
      });
      if (res.ok) {
        setViewerMarkdown(editorText);
        alert('Saved');
      } else {
        const t = await res.text();
        alert(`Save failed: ${t}`);
      }
    } catch (e) {
      alert(`Save error: ${String(e)}`);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging) return;
      const vw = window.innerWidth || 1;
      const x = e.clientX;
      const newPct = Math.max(50, Math.min(75, ((vw - x) / vw) * 100));
      setWidthPct(newPct);
    };
    const onUp = () => setDragging(false);
    if (dragging) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  // Dragging for job viewer
  useEffect(() => {
    const onMove = (e) => {
      if (!jobViewerOpen || !jobDragRef.current) return;
      const vw = window.innerWidth || 1;
      const x = e.clientX;
      const newPct = Math.max(50, Math.min(75, ((vw - x) / vw) * 100));
      setJobWidthPct(newPct);
    };
    const onUp = () => { /* no local state */ };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [jobViewerOpen]);

  const openJobViewer = async (doc) => {
    try { if (jobPollRef.current) clearInterval(jobPollRef.current); } catch {}
    setJobDoc(doc);
    setJobDetail(null);
    setJobViewerOpen(true);
    const headers = { ...getAuthHeaders() };
    const fetchOnce = async () => {
      if (!doc?.job_id) return;
      try {
        const r = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${doc.job_id}`, { headers });
        if (r.ok) setJobDetail(await r.json());
      } catch (_) { /* ignore */ }
    };
    await fetchOnce();
    jobPollRef.current = setInterval(fetchOnce, 5000);
  };

  const closeJobViewer = () => {
    try { if (jobPollRef.current) clearInterval(jobPollRef.current); } catch {}
    setJobViewerOpen(false);
    setJobDoc(null);
    setJobDetail(null);
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/${doc.id}`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (res.ok) loadDocs();
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="input-group" style={{ maxWidth: 480 }}>
              <input className="form-control" placeholder="Search by filename, job or batch" value={q} onChange={(e) => setQ(e.target.value)} />
              <button className="btn btn-outline-primary" onClick={() => { setOffset(0); loadDocs(); }}>Search</button>
            </div>
            <div className="d-flex align-items-center gap-2">
              <select className="form-select form-select-sm" style={{ width: 90 }} value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <div className="text-muted small">Total: {docs.total}</div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Vector ID</th>
                  <th>Status</th>
                  <th>Size</th>
                  <th>Progress</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(docs.items || []).map((d) => (
                  <tr key={d.id}>
                    <td>{d.original_filename}</td>
                    <td style={{ wordBreak: 'break-all' }}>{d.pinecone_vector_id || '-'}</td>
                    <td>
                      <span
                        role="button"
                        className={`badge ${String(d.status||'').toLowerCase()==='completed'?'bg-success':String(d.status||'').toLowerCase()==='failed'?'bg-danger':String(d.status||'').toLowerCase()==='cancelled'?'bg-secondary':'bg-warning text-dark'}`}
                        title="View processing details"
                        onClick={() => openJobViewer(d)}
                      >
                        {d.status}
                      </span>
                    </td>
                    <td>{typeof d.file_size === 'number' ? `${(d.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}</td>
                    <td>{d.progress}%</td>
                    <td>{new Date(d.created_at).toLocaleString()}</td>
                    <td className="text-nowrap" style={{ minWidth: 180 }}>
                      <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-outline-primary btn-sm" disabled={!d.markdown_url} onClick={() => viewMarkdown(d)}>View</button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => deleteDocument(d)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!docs.items || docs.items.length === 0) && (
                  <tr><td colSpan="6" className="text-center text-muted py-3">{loading ? 'Loading…' : 'No documents found'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-outline-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Prev</button>
            <button className="btn btn-outline-secondary btn-sm" disabled={(offset + (docs.items?.length || 0)) >= docs.total} onClick={() => setOffset(offset + limit)}>Next</button>
          </div>
        </div>
      </div>
      {viewerOpen && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1049 }} onClick={() => setViewerOpen(false)} />
          <div className="position-fixed top-0 end-0 h-100 bg-white shadow" style={{ width: `${widthPct}vw`, maxWidth: '80vw', minWidth: '50vw', zIndex: 1050, transition: 'transform 0.25s ease', transform: 'translateX(0)' }}>
            <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2">
              <div className="fw-semibold text-truncate" title={viewerTitle}>{viewerTitle}</div>
              <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch me-2">
                  <input className="form-check-input" type="checkbox" id="toggleEditor"
                    checked={editorMode} onChange={toggleEditor} />
                  <label className="form-check-label small" htmlFor="toggleEditor">Editor</label>
                </div>
                {editorMode && (
                  <button className="btn btn-sm btn-primary" onClick={saveMarkdown}>Save</button>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setViewerOpen(false)}>Close</button>
              </div>
            </div>
            <div className="position-absolute top-0 start-0 h-100" style={{ width: 6, cursor: 'ew-resize', zIndex: 1051 }} onMouseDown={() => setDragging(true)} ref={dragRef} />
            <div className="p-3 overflow-auto h-100" style={{ paddingTop: 12 }}>
              {viewerLoading ? (
                <div className="text-muted">Loading…</div>
              ) : editorMode ? (
                <textarea className="form-control" style={{height:'100%', minHeight: '60vh'}} value={editorText} onChange={(e) => setEditorText(e.target.value)} />
              ) : (
                <div className="markdown-body">
                  <ReactMarkdown
                    components={{
                      img: ({node, ...props}) => {
                        try {
                          const src = props.src || '';
                          const isAbsolute = /^https?:\/\//i.test(src);
                          const resolved = isAbsolute ? src : `${viewerBaseDir}/${src.replace(/^\.\//,'')}`;
                          return <img alt={props.alt || ''} src={resolved} style={{maxWidth:'100%'}} />
                        } catch (_) {
                          return <img alt={props.alt || ''} {...props} style={{maxWidth:'100%'}} />
                        }
                      }
                    }}
                  >{viewerMarkdown}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {jobViewerOpen && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: 'rgba(0,0,0,0.4)', zIndex: 1049 }} onClick={closeJobViewer} />
          <div className="position-fixed top-0 end-0 h-100 bg-white shadow" style={{ width: `${jobWidthPct}vw`, maxWidth: '75vw', minWidth: '50vw', zIndex: 1050, transition: 'transform 0.25s ease', transform: 'translateX(0)' }}>
            <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2">
              <div className="fw-semibold text-truncate" title={jobDoc?.original_filename || ''}>
                {jobDoc?.original_filename || 'Job'}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={closeJobViewer}>Close</button>
              </div>
            </div>
            <div className="position-absolute top-0 start-0 h-100" style={{ width: 6, cursor: 'ew-resize', zIndex: 1051 }} onMouseDown={() => (jobDragRef.current = true)} onMouseUp={() => (jobDragRef.current = false)} />
            <div className="p-3 overflow-auto h-100" style={{ paddingTop: 12 }}>
              {!jobDetail ? (
                <div className="text-muted">Loading…</div>
              ) : (
                <div>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <span className={`badge ${String(jobDetail.status||'').toLowerCase()==='completed'?'bg-success':String(jobDetail.status||'').toLowerCase()==='failed'?'bg-danger':String(jobDetail.status||'').toLowerCase()==='cancelled'?'bg-secondary':'bg-warning text-dark'}`}>{jobDetail.status}</span>
                    <div className="progress" style={{ width: 160 }}>
                      <div className="progress-bar" style={{ width: `${Number(jobDetail.progress||0)}%` }}>{Number(jobDetail.progress||0)}%</div>
                    </div>
                  </div>
                  {Array.isArray(jobDetail?.metadata?.pipeline) ? (
                    <ul className="list-group">
                      {jobDetail.metadata.pipeline.map((s, i) => (
                        <li key={i} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold">{s.name}</div>
                              <div className="small text-muted">{s.message}</div>
                              <div className="small text-muted">
                                {s.started_at && <span className="me-3">Start: {s.started_at}</span>}
                                {s.finished_at && <span className="me-3">End: {s.finished_at}</span>}
                                {typeof s.duration_seconds === 'number' && <span>Duration: {s.duration_seconds.toFixed(1)}s</span>}
                              </div>
                            </div>
                            <span className={`badge ${s.status==='completed'?'bg-success':s.status==='failed'?'bg-danger':'bg-warning text-dark'}`}>{s.status}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="small text-muted">No step details yet.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentsListV4;


